// ============================================================================
// Question Pre-Cache Service - Download Question Banks for Offline Use
// EduPath Africa v2.0 - Sprint 3: Offline Assessment Infrastructure
// ============================================================================
//
// FILE: src/services/questionPreCache.ts
//
// Downloads and caches all question banks to IndexedDB when the user logs in.
// Questions are served from cache when offline, bypassing the API entirely.
//
// Usage:
//   - Called automatically after successful login (wire into authStore)
//   - Can be triggered manually: preCacheAllQuestionBanks()
//   - Individual types: preCacheQuestionBank('RIASEC')
//
// Cache invalidation:
//   - Stale after 7 days (configurable)
//   - Version-checked against server (when online)
//   - Manual clear via questionBankCache.clear()
// ============================================================================

import { questionBankCache, metaStore } from './offlineStore';
import { isOnline } from '@/hooks/useNetworkStatus';
import type { CachedQuestion } from './offlineStore';

// ============================================================================
// Constants
// ============================================================================

const API_BASE = import.meta.env.VITE_API_URL ||
  'https://65563g1ia9.execute-api.eu-west-1.amazonaws.com/dev';

// All assessment types that should be pre-cached
const ASSESSMENT_TYPES = [
  'RIASEC',
  'BIG_FIVE',
  'NUMERICAL',
  'VERBAL',
  'SPATIAL',
] as const;

// Optional: VALUES_RESILIENCE if feature flag is on
const OPTIONAL_TYPES = ['VALUES_RESILIENCE'] as const;

const CACHE_MAX_AGE_DAYS = 7;

// ============================================================================
// Types
// ============================================================================

interface QuestionBankApiResponse {
  assessmentType: string;
  questions: Array<{
    questionId?: string;
    id?: string;
    questionText?: string;
    text?: string;
    responseType?: string;
    type?: string;
    dimension?: string;
    construct?: string;
    options?: string[];
    answerOptions?: string[];
    choices?: string[];
    responseOptions?: Array<{ label?: string; text?: string }>;
  }>;
  version?: string;
  count?: number;
}

export interface PreCacheProgress {
  total: number;
  completed: number;
  current: string;
  errors: string[];
}

type ProgressCallback = (progress: PreCacheProgress) => void;

// ============================================================================
// Pre-Cache Functions
// ============================================================================

/**
 * Pre-cache all question banks. Call after login.
 * Skips types that are already cached and fresh.
 */
export async function preCacheAllQuestionBanks(
  onProgress?: ProgressCallback
): Promise<PreCacheProgress> {
  // Determine which types to cache
  const typesToCache = [...ASSESSMENT_TYPES];

  // Add VALUES_RESILIENCE if enabled
  try {
    const vrEnabled = import.meta.env.VITE_FF_VALUES_RESILIENCE_ENABLED === 'true';
    if (vrEnabled) {
      typesToCache.push(...OPTIONAL_TYPES);
    }
  } catch {
    // Feature flag not available
  }

  const progress: PreCacheProgress = {
    total: typesToCache.length,
    completed: 0,
    current: '',
    errors: [],
  };

  if (!isOnline()) {
    console.log('[PreCache] Offline — skipping question bank download');
    return progress;
  }

  console.log(`[PreCache] Starting pre-cache for ${typesToCache.length} assessment types`);

  for (const type of typesToCache) {
    progress.current = type;
    onProgress?.(progress);

    try {
      // Check if already cached and fresh
      const isFresh = await questionBankCache.isFresh(type, CACHE_MAX_AGE_DAYS);
      if (isFresh) {
        console.log(`[PreCache] ${type} — already cached and fresh, skipping`);
        progress.completed++;
        continue;
      }

      await preCacheQuestionBank(type);
      progress.completed++;
    } catch (err) {
      const msg = `Failed to cache ${type}: ${err instanceof Error ? err.message : 'Unknown error'}`;
      console.warn(`[PreCache] ${msg}`);
      progress.errors.push(msg);
      progress.completed++; // Count as processed even if failed
    }
  }

  // Record last sync time
  await metaStore.set('lastQuestionBankSync', new Date().toISOString());

  console.log(
    `[PreCache] Complete: ${progress.completed - progress.errors.length}/${progress.total} cached, ${progress.errors.length} errors`
  );

  return progress;
}

/**
 * Pre-cache a single question bank type
 */
export async function preCacheQuestionBank(assessmentType: string): Promise<number> {
  console.log(`[PreCache] Downloading ${assessmentType} question bank...`);

  // Get auth token
  let token: string | null = null;
  try {
    const stored = localStorage.getItem('edupath-auth');
    if (stored) {
      const parsed = JSON.parse(stored);
      token = parsed?.state?.accessToken || null;
    }
  } catch {
    // No token
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(
    `${API_BASE}/assessments/questions?type=${encodeURIComponent(assessmentType)}`,
    { headers, cache: 'no-store' }
  );

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const data: QuestionBankApiResponse = await response.json();
  const rawQuestions = data.questions || [];

  // Normalize to CachedQuestion format
  const normalized: CachedQuestion[] = rawQuestions.map((q) => ({
    id: q.questionId || q.id || '',
    text: q.questionText || q.text || '',
    type: normalizeType(q.responseType || q.type, assessmentType),
    options:
      q.options ||
      q.answerOptions ||
      q.choices ||
      (q.responseOptions && q.responseOptions.map((ro) => ro.label || ro.text || '')) ||
      undefined,
    dimension: q.dimension || q.construct,
    assessmentType,
  }));

  // Save to IndexedDB
  await questionBankCache.save(assessmentType, normalized, data.version || '1.0');

  console.log(`[PreCache] Cached ${normalized.length} questions for ${assessmentType}`);
  return normalized.length;
}

/**
 * Get questions for an assessment, preferring cache over network
 */
export async function getQuestionsOfflineFirst(
  assessmentType: string
): Promise<CachedQuestion[]> {
  // Try cache first
  const cached = await questionBankCache.get(assessmentType);

  if (cached && cached.questions.length > 0) {
    console.log(`[PreCache] Serving ${assessmentType} from cache (${cached.questions.length} questions)`);
    return cached.questions;
  }

  // Fall back to network
  if (isOnline()) {
    console.log(`[PreCache] Cache miss for ${assessmentType}, fetching from network`);
    await preCacheQuestionBank(assessmentType);
    const freshCache = await questionBankCache.get(assessmentType);
    return freshCache?.questions || [];
  }

  // Offline and no cache — return empty (UI should handle this gracefully)
  console.warn(`[PreCache] No cached questions for ${assessmentType} and offline`);
  return [];
}

/**
 * Check cache freshness for all types
 */
export async function getCacheStatus(): Promise<
  Array<{ type: string; cached: boolean; fresh: boolean; questionCount: number; cachedAt: string | null }>
> {
  const status = [];
  const allTypes = [...ASSESSMENT_TYPES, ...OPTIONAL_TYPES];

  for (const type of allTypes) {
    const cached = await questionBankCache.get(type);
    const fresh = await questionBankCache.isFresh(type, CACHE_MAX_AGE_DAYS);
    status.push({
      type,
      cached: !!cached,
      fresh,
      questionCount: cached?.questionCount || 0,
      cachedAt: cached?.cachedAt || null,
    });
  }

  return status;
}

// ============================================================================
// Helpers
// ============================================================================

const MCQ_TYPES = new Set(['NUMERICAL', 'VERBAL', 'SPATIAL']);

function normalizeType(
  responseType: string | undefined,
  assessmentType: string
): 'likert' | 'multiple_choice' {
  if (responseType) {
    const rt = responseType.toUpperCase();
    if (rt.includes('CHOICE') || rt.includes('MCQ') || rt === 'MULTIPLE_CHOICE') {
      return 'multiple_choice';
    }
    if (rt.includes('LIKERT')) return 'likert';
  }
  return MCQ_TYPES.has(assessmentType.toUpperCase()) ? 'multiple_choice' : 'likert';
}
